// AS AUTORIZACOES DO SERVIDOR NA COMPETENCIA (#282), no portal do orgao.
//
// Quem autorizou o que, quando e por qual canal — e o que ainda espera ou
// expirou. Sem CPF: a matricula basta para o orgao conferir.

import { useQuery } from "@tanstack/react-query";
import { Button, Card, DataTable, EstadoVazio, Pill, fmtBRL, type Column } from "@atlas/ui/web";
import type { AutorizacaoDaCompetencia } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { useUnidadeAtiva } from "../../lib/unidade-ativa";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { downloadAuthed } from "./_ui";

const ROTULO_STATUS: Record<AutorizacaoDaCompetencia["status"], { label: string; variant: "aceita" | "pendente" | "expirado" }> = {
  autorizada: { label: "Autorizada", variant: "aceita" },
  aguardando: { label: "Aguardando o servidor", variant: "pendente" },
  expirada: { label: "Expirada", variant: "expirado" },
};

export function AutorizacoesDoServidor({ competencia }: { competencia: string }) {
  const { opcoesDoSdk, chaveDaUnidade } = useUnidadeAtiva();
  const valida = /^\d{4}(0[1-9]|1[0-2])$/.test(competencia);
  const q = useQuery({
    queryKey: ["prefeitura", "autorizacoes", competencia, ...chaveDaUnidade()],
    queryFn: () => atlas.prefeitura.autorizacoes.listar(competencia, opcoesDoSdk()),
    enabled: valida,
  });
  const itens = q.data?.itens ?? [];
  if (!valida || (q.isSuccess && itens.length === 0)) return null;
  const colunas: Column<AutorizacaoDaCompetencia>[] = [
    { key: "matricula", header: "Matrícula", mono: true, render: (a) => a.matricula },
    { key: "rubrica", header: "Rubrica", mono: true, render: (a) => a.rubrica },
    { key: "tipo", header: "Tipo", render: (a) => (a.tipo === "inclusao" ? "Inclusão" : "Aumento") },
    { key: "valor", header: "Valor", align: "right", render: (a) => fmtBRL(a.valorCentavos / 100) },
    {
      key: "status",
      header: "Situação",
      render: (a) => <Pill variant={ROTULO_STATUS[a.status].variant}>{ROTULO_STATUS[a.status].label}</Pill>,
    },
    {
      key: "quando",
      header: "Quando",
      render: (a) =>
        a.autorizadaEm
          ? `${new Date(a.autorizadaEm).toLocaleString("pt-BR")} · ${a.canal === "token" ? "código" : "tela"}`
          : `até ${a.prazoAte.split("-").reverse().join("/")}`,
    },
  ];
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: "1.05rem" }}>Autorizações do servidor</h2>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void downloadAuthed(atlas.prefeitura.autorizacoes.csvUrl(competencia, opcoesDoSdk()), `autorizacoes-${competencia}.csv`)}
        >
          Exportar CSV
        </Button>
      </div>
      <p style={{ margin: "4px 0 12px", fontSize: 13, color: "var(--text-muted)" }}>
        Inclusões e aumentos de descontos facultativos que dependem da autorização do servidor.
      </p>
      {q.isError && <p role="alert">{mensagemDeErro(q.error, "Não foi possível carregar as autorizações.")}</p>}
      <DataTable
        caption="Autorizações do servidor"
        columns={colunas}
        rows={itens}
        rowKey={(a) => String(a.id)}
        loading={q.isLoading}
        emptyState={<EstadoVazio titulo="Nenhuma autorização nesta competência." />}
      />
    </Card>
  );
}
